import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";

/** One bubble's worth of lines, at the widths a real message runs to. */
const OWNER = ["w-40", "w-52"];
const REPLY = ["w-full", "w-11/12", "w-4/5", "w-2/3"];

/**
 * The conversation while its messages load.
 *
 * There was no loading state here at all, so an unanswered query fell through
 * to the empty one and a thread with five messages in it said "What should
 * change? Describe the rule and which employees it affects." on every reload.
 * An empty state shown while loading is a lie about the data, not a delay.
 *
 * It reads bottom-up, the way the transcript does: the newest exchange is at
 * the foot, against the composer, and the older lines fade as they rise.
 *
 * When a request is already running it says so straight away. The workspace
 * payload carries `active_request`, and that answers before the messages do, so
 * a reader who reloads mid-request is told their request is still going instead
 * of watching grey bars for the second it takes the transcript to arrive.
 */
export const TranscriptSkeleton = ({
  working = false,
}: {
  working?: boolean;
}) => (
  <Stack
    gap="none"
    align="stretch"
    direction="column"
    role="status"
    aria-label={
      working ? "Working on your request…" : "Loading your conversation…"
    }
    className="flex min-h-0 flex-1 flex-col justify-end gap-4 px-5 py-5"
  >
    <Stack
      gap="none"
      align="stretch"
      direction="column"
      aria-hidden
      className="flex flex-col items-end gap-2 opacity-40"
    >
      {OWNER.map((width) => (
        <Skeleton key={width} className={cn("h-3", width)} />
      ))}
    </Stack>
    <Stack
      gap="none"
      align="stretch"
      direction="column"
      aria-hidden
      className="flex flex-col gap-2 opacity-60"
    >
      {REPLY.map((width) => (
        <Skeleton key={width} className={cn("h-3", width)} />
      ))}
    </Stack>
    <Stack
      gap="none"
      align="stretch"
      direction="column"
      aria-hidden
      className="flex flex-col items-end gap-2"
    >
      {OWNER.map((width) => (
        <Skeleton key={width} className={cn("h-3", width)} />
      ))}
    </Stack>
    {working && (
      <Stack
        gap="none"
        align="stretch"
        direction="column"
        className="flex flex-col gap-1"
      >
        <p className="flex items-center gap-2 text-secondary-text">
          <span
            aria-hidden
            className="h-1.5 w-1.5 animate-pulse rounded-full bg-stroke-interactive"
          />
          Working on your request…
        </p>
      </Stack>
    )}
  </Stack>
);
